import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TrainingAPI } from "../../api/TrainingAPI";
import { useSelector } from "react-redux";

import "bootstrap/dist/css/bootstrap.min.css";
import { Table, Tooltip } from "antd";
import { audioMessage as message } from '../Common/Notifications/audioMessage';

import "../../Styles/styles-PostData.css";
import "../../Styles/styles-Settings.css";
import { useDispatch, useSelector as useReduxSelector } from "react-redux";
import { getTeamPlayers, setTeamPresets, getTeamPresets, validateTeamAndNotify } from "../../store/actions/team.actions";
import { 
  PermissionedInputNumber, 
  PermissionedButton 
} from "../PermissionedComponents";
import PermissionViewable from "../PermissionedComponents/PermissionViewable";
import BuildBaselineModal from "./BuildBaselineModal/BuildBaselineModal";
import { PresetSelection, PresetType } from "../PresetSelection.tsx";
import { useAuth0 } from "@auth0/auth0-react";
import { PlayerLevelBaslineMetrics } from './BuildBaselineModal/baseline_util.js'

export const TeamBaseline = ({
  allMetrics,
  sport,
  getBaselineOverviewData,
}) => {
  const trainingAPI = new TrainingAPI();
  const PRESET_TYPE = PresetType.Baseline;
  const DEFAULT_PRESET = "Default";

  const { positions: teamPositions } = useSelector(({ team }) => team);
  const { players: teamPlayers, presets } = useReduxSelector(({ team }) => team || {});
  const dispatch = useDispatch();
  const { user } = useAuth0();

  useEffect(() => {
    // Ensure players list is present for position counts
    try {
      dispatch && dispatch(getTeamPlayers());
    } catch (e) {
      // best-effort; ignore if action not available
    }
  }, [dispatch]);

  // Fetch baseline presets on mount
  useEffect(() => {
    dispatch(getTeamPresets({ type: [PRESET_TYPE] }));
  }, [dispatch]);

  const positionCounts = useMemo(() => {
    const counts = {};
    if (Array.isArray(teamPlayers)) {
      teamPlayers.forEach((p) => {
        const pos = p?.position ?? p?.player_position ?? p?.pos;
        const retired = !!(p?.retired);
        if (!pos || retired) return;
        counts[pos] = (counts[pos] || 0) + 1;
      });
    }
    return counts;
  }, [teamPlayers]);

  // Flatten position options to [{ value, label }]
  const positionOptions = useMemo(() => {
    const out = [];
    const pushVal = (value, label) => {
      if (value === undefined || value === null) return;
      out.push({ value, label: label ?? String(value) });
    };
    const dfs = (nodes) => {
      if (!nodes) return;
      nodes.forEach((n) => {
        if (n == null) return;
        if (typeof n === 'string' || typeof n === 'number') {
          pushVal(n, String(n));
          return;
        }
        const maybeValue = n.value ?? n.key ?? n.id;
        const maybeLabel = n.label ?? n.name ?? n.title ?? String(maybeValue);
        if (Array.isArray(n.options) && n.options.length) {
          dfs(n.options);
        } else if (Array.isArray(n.children) && n.children.length) {
          dfs(n.children);
        } else if (maybeValue !== undefined) {
          pushVal(maybeValue, maybeLabel);
        }
      });
    };
    if (Array.isArray(teamPositions)) {
      dfs(teamPositions);
    }
    // de-duplicate by value
    const seen = new Set();
    return out.filter(({ value }) => (seen.has(value) ? false : (seen.add(value), true)));
  }, [teamPositions]);

  // Metrics to show as columns (filtering like before)
  const filteredMetrics = useMemo(
    () =>
      ([...(allMetrics || []), ...PlayerLevelBaslineMetrics]).filter(
        (metric) =>
          ![
            "aerobic_training_effect",
            "anaerobic_training_effect",
            // "top_speed_percent",
            "RPE",
            "session_RPE"
          ].includes(metric.key)
      ),
    [allMetrics]
  );

  // Averages (position rows, Team Avg, previews) keep at least 2 places and never fewer
  // than the metric's configured decimal places, so a 4-place baseline survives the display.
  const roundAverage = useCallback(
    (value, metricKey) => {
      const metric = filteredMetrics.find(({ key }) => key === metricKey);
      const places = Math.max(Number(metric?.decimal_places) || 0, 2);
      const factor = 10 ** places;
      return Math.round(value * factor) / factor;
    },
    [filteredMetrics]
  );

  const [refetchData, setRefetchData] = useState(false)

  // Table data and helpers
  const [dataSource, setDataSource] = useState([]); // [{ key: positionValue, positionLabel, values... }]
  const [loading, setLoading] = useState(false);
  const [buildBaselineModalVisible, setBuildBaselineModalVisible] = useState(false)
  const originalValuesRef = useRef({}); // { [positionValue]: { metricKey: number } }

  // Track dirty cells per row: { [positionValue]: Set(metricKey) }
  const dirtyMapRef = useRef({});
  const [dirtyCounter, setDirtyCounter] = useState(0);

  const [currentSelectedPreset, setCurrentSelectedPreset] = useState(DEFAULT_PRESET);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const markDirty = (positionValue, metricKey, isDirty) => {
    if (!dirtyMapRef.current[positionValue]) dirtyMapRef.current[positionValue] = new Set();
    if (isDirty) dirtyMapRef.current[positionValue].add(metricKey);
    else dirtyMapRef.current[positionValue].delete(metricKey);
  };

  const isDirtyCell = (positionValue, metricKey) => {
    const s = dirtyMapRef.current[positionValue];
    return !!(s && s.has(metricKey));
  };

  const loadAllBaselines = async () => {
    if (!positionOptions.length) return;
    setLoading(true);
    try {
      const requests = positionOptions.map((pos) =>
        trainingAPI
          .getBaseline(pos.value)
          .then((playerBaselines) => ({ pos, playerBaselines }))
          .catch(() => ({ pos, playerBaselines: [] }))
      );
      const results = await Promise.all(requests);

      const nextData = results.map(({ pos, playerBaselines }) => {
        const positionName = pos.label;
        const count = positionCounts[positionName] ?? positionCounts[pos.value] ?? 0;
        const label = count ? `${positionName} (${count})` : positionName;
        const row = { key: String(pos.value), positionLabel: label, positionValue: pos.value, positionName };

        const playerBaselinesMap = new Map();
        if (Array.isArray(playerBaselines)) {
          playerBaselines.forEach((baseline) => {
            playerBaselinesMap.set(baseline.player_id, baseline);
          });
        }

        // Add player rows as children
        const playersForPosition = (teamPlayers || []).filter((p) => {
          const playerPos = p?.position ?? p?.player_position ?? p?.pos;
          const retired = !!(p?.retired);
          return !retired && (playerPos === pos.value || playerPos === positionName);
        });

        row.children = playersForPosition.map((player) => {
          const playerRow = {
            key: `player_${player.player_id}`,
            positionLabel: `${player.first_name} ${player.last_name}`,
            positionValue: player.player_id,
            positionName,
            isPlayer: true,
            parentPosition: pos.value,
          };

          const playerBaseline = playerBaselinesMap.get(player.player_id);
          filteredMetrics.forEach(({ key }) => {
            const value = playerBaseline?.[key];
            playerRow[key] = value !== undefined && value !== null 
              ? Number(value) 
              : 0;
          });

          return playerRow;
        });

        // Calculate position row values as average of all player values
        if (row.children.length > 0) {
          filteredMetrics.forEach(({ key }) => {
            const playerValues = row.children.map((child) => Number(child[key] ?? 0));
            const sum = playerValues.reduce((acc, val) => acc + (Number.isFinite(val) ? val : 0), 0);
            const avg = playerValues.length > 0 ? sum / playerValues.length : 0;
            row[key] = roundAverage(avg, key);
          });
        } else {
          filteredMetrics.forEach(({ key }) => {
            row[key] = 0;
          });
        }

        return row;
      });

      // Prepend Team Avg row (uneditable) - weighted by players and positions
      const teamAvg = { key: "__TEAM_AVG__", positionLabel: "Team Avg*", positionValue: "__TEAM_AVG__", isTeamAvg: true };
      filteredMetrics.forEach(({ key }) => {
        let weightedSum = 0;
        let totalWeight = 0;
        nextData.forEach((r) => {
          const posName = r.positionName || r.positionLabel;
          const w = positionCounts[posName] ?? positionCounts[r.positionValue] ?? 0;
          const val = Number(r[key] ?? 0);
          if (Number.isFinite(val)) {
            weightedSum += val * (w || 0);
            totalWeight += (w || 0);
          }
        });
        const avg = totalWeight > 0 ? roundAverage(weightedSum / totalWeight, key) : 0;
        teamAvg[key] = avg;
      });
      const withAvg = [teamAvg, ...nextData];

      originalValuesRef.current = {};
      withAvg.forEach((row) => {
        originalValuesRef.current[row.positionValue] = filteredMetrics.reduce((m, { key }) => {
          m[key] = row[key];
          return m;
        }, {});

        row.children?.forEach((child) => {
          originalValuesRef.current[child.positionValue] = filteredMetrics.reduce((m, { key }) => {
            m[key] = child[key];
            return m;
          }, {});
        });
      });
      
      dirtyMapRef.current = {};

      setDataSource(withAvg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllBaselines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionOptions.length, filteredMetrics.length, JSON.stringify(positionCounts)]);

  useEffect(() => {
    if (refetchData) {
      loadAllBaselines();
      setRefetchData(false)
    }
  }, [refetchData])

  const compareWithOriginalBaseline = (presetData) => {
    const differences = [];
    
    presetData.forEach((presetRow) => {
      const positionValue = presetRow.positionValue;
      const originalRow = originalValuesRef.current[positionValue];
      
      if (!originalRow) return;
      
      filteredMetrics.forEach(({ key, name }) => {
        const presetValue = presetRow[key] ?? 0;
        const originalValue = originalRow[key] ?? 0;
        
        if (presetValue !== originalValue) {
          differences.push({
            position: presetRow.positionLabel || positionValue,
            metric: name || key,
            presetValue,
            originalValue,
          });
        }
      });
    });
    
    return differences;
  };

  useEffect(() => {
    if (currentSelectedPreset === DEFAULT_PRESET) {
      loadAllBaselines();
    } else {
      const selectedPreset = presets?.find((preset) => preset.name === currentSelectedPreset);
      if (selectedPreset?.config && Array.isArray(selectedPreset.config)) {
        // Clear dirty state when loading preset
        dirtyMapRef.current = {};
  
        const teamAvg = { key: "__TEAM_AVG__", positionLabel: "Team Avg*", positionValue: "__TEAM_AVG__", isTeamAvg: true };
        filteredMetrics.forEach(({ key }) => {
          let weightedSum = 0;
          let totalWeight = 0;
          selectedPreset.config.forEach((r) => {
            const posName = r.positionName || r.positionLabel;
            const w = positionCounts[posName] ?? positionCounts[r.positionValue] ?? 0;
            const val = Number(r[key] ?? 0);
            if (Number.isFinite(val)) {
              weightedSum += val * (w || 0);
              totalWeight += (w || 0);
            }
          });
          const avg = totalWeight > 0 ? roundAverage(weightedSum / totalWeight, key) : 0;
          teamAvg[key] = avg;
        });

        const dataWithAvg = [teamAvg, ...selectedPreset.config];

        const allRowsToCheck = [];
        selectedPreset.config.forEach((presetRow) => {
          allRowsToCheck.push(presetRow);
          if (presetRow.children) {
            allRowsToCheck.push(...presetRow.children);
          }
        });


        let hasAnyDifferences = false;
        allRowsToCheck.forEach((presetRow) => {
          const positionValue = presetRow.positionValue;
          const originalRow = originalValuesRef.current[positionValue];
          
          if (!originalRow) return;
          
          filteredMetrics.forEach(({ key }) => {
            const presetValue = presetRow[key] ?? 0;
            const originalValue = originalRow[key] ?? 0;
            
            if (presetValue !== originalValue) {
              markDirty(positionValue, key, true);
              hasAnyDifferences = true;
            }
          });
        });

        setDataSource(dataWithAvg);
        setUnsavedChanges(hasAnyDifferences);
        if (hasAnyDifferences) {
          setDirtyCounter(prev => prev + 1); // Trigger re-render to show dirty cells
        }
      }
    }
  }, [currentSelectedPreset]);

  const onCellChange = (positionValue, metricKey, nextValue, isPlayerRow = false, parentPosition = null) => {
    setDataSource((prev) =>
      prev.map((row) => {
        // Handle player row change
        if (isPlayerRow && row.children) {
          const updatedChildren = row.children.map((child) => {
            if (child.positionValue !== positionValue) return child;
            
            const numeric = Number(nextValue);
            const safe = Number.isFinite(numeric) ? numeric : 0;
            const original = originalValuesRef.current[child.positionValue]?.[metricKey] ?? 0;
            const changed = safe !== original;
            markDirty(child.positionValue, metricKey, changed);
            
            return { ...child, [metricKey]: safe };
          });
          
          return { ...row, children: updatedChildren };
        }
        
        // Handle position row change
        if (!isPlayerRow && row.positionValue === positionValue) {
          const numeric = Number(nextValue);
          const safe = Number.isFinite(numeric) ? numeric : 0;
          const original = originalValuesRef.current[positionValue]?.[metricKey] ?? 0;
          const changed = safe !== original;
          markDirty(positionValue, metricKey, changed);
          
          return { ...row, [metricKey]: safe };
        }
        
        return row;
      })
    );
    setUnsavedChanges(true);
  };

  const saveChanges = async () => {
    const changedPositions = new Set();
    const changedPlayers = new Map(); // player_id -> player row
    
    dataSource.forEach((row) => {
      if (row.isTeamAvg) return;
      
      // check position row
      if ((dirtyMapRef.current[row.positionValue]?.size || 0) > 0) {
        changedPositions.add(row.positionValue);
      }
      
      // check child player rows
      row?.children?.forEach((child) => {
        if ((dirtyMapRef.current[child.positionValue]?.size || 0) > 0) {
          changedPlayers.set(child.positionValue, child);
        }
      });
    });
    
    if (changedPositions.size === 0 && changedPlayers.size === 0) {
      message.success("No changes to save");
      return;
    }

    const playersToSave = [];
    
    dataSource.forEach((row) => {
      if (row.isTeamAvg || !row.children) return;
      
      // If position value changed, include all players for that position
      if (changedPositions.has(row.positionValue)) {
        const changedMetrics = dirtyMapRef.current[row.positionValue] || new Set();
        
        row.children.forEach((child) => {
          const playerBaselines = filteredMetrics.reduce((acc, { key }) => {
            if (changedMetrics.has(key)) {
              // apply position value specified by user to player baselines
              acc[key] = row[key] ?? 0;
            } else {
              acc[key] = child[key] ?? 0;
            }
            return acc;
          }, {});
          
          playersToSave.push({
            player_id: child.positionValue,
            position: row.positionValue,
            ...playerBaselines,
          });
        });
      }
    });
    
    // Add individually changed players (if not already included from position changes)
    const includedPlayerIds = new Set(playersToSave.map(p => p.player_id));
    changedPlayers.forEach((playerRow, playerId) => {
      if (!includedPlayerIds.has(playerId)) {
        const playerBaselines = filteredMetrics.reduce((acc, { key }) => {
          acc[key] = playerRow[key] ?? 0;
          return acc;
        }, {});

        playersToSave.push({
          player_id: playerId,
          position: playerRow.parentPosition,
          ...playerBaselines,
        });
      }
    });

    for (const player of playersToSave) {
      const invalidCell = filteredMetrics.some(({ key }) => {
        const v = player[key];
        return v === undefined || v === '' || !Number.isFinite(Number(v));
      });
      if (invalidCell) {
        message.error(`Please enter valid numbers for all baselines for player: ${player.player_id}`);
        return;
      }
    }

    setLoading(true);
    try {

      await trainingAPI.updateBaseline({ baselines: playersToSave }).catch((e) => {
        message.error(`Failed to save baselines`);
        throw e;
      });

      message.success("Baselines updated");
      setUnsavedChanges(false);

      // Repull from server to ensure freshness and clear highlights
      await loadAllBaselines();
      getBaselineOverviewData && getBaselineOverviewData();

      await dispatch(validateTeamAndNotify());
    } catch (e) {
      // already messaged per-row; keep generic here
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreset = async () => {
    try {
      if (currentSelectedPreset === DEFAULT_PRESET) {
        message.info("Use 'Save Changes' button to update baseline values");
        return;
      }
      
      const presetModified = presets?.find((preset) => preset.name === currentSelectedPreset);
      if (!presetModified) {
        message.error("Preset not found");
        return;
      }

      const configToSave = dataSource.filter(row => !row.isTeamAvg);
      
      await trainingAPI.updatePreset({
        name: currentSelectedPreset,
        type: PRESET_TYPE,
        config: configToSave,
        preset_id: presetModified.preset_id,
      });
      
      presetModified.config = configToSave;
      dispatch(setTeamPresets(presets));
      
      message.success("Preset saved!");
      setUnsavedChanges(false);
    } catch (e) {
      message.error("Error saving preset");
      console.error(e);
    }
  };

  const getPreviewValue = (record, metricKey) => {
    if (record.isPlayer) {
      const parentRow = dataSource.find(row => row.positionValue === record.parentPosition);
      if (!parentRow) return null;

      const parentDirty = isDirtyCell(parentRow.positionValue, metricKey);
      if (!parentDirty) return null;

      return parentRow[metricKey];
    }
    
    if (!record.isPlayer && !record.isTeamAvg && record.children && record.children.length > 0) {
      const positionDirty = isDirtyCell(record.positionValue, metricKey);
      if (positionDirty) return null;
      
      const hasChildChanges = record.children.some(child => 
        isDirtyCell(child.positionValue, metricKey)
      );
      
      if (!hasChildChanges) return null;

      const playerValues = record.children.map(child => Number(child[metricKey] ?? 0));
      const sum = playerValues.reduce((acc, val) => acc + (Number.isFinite(val) ? val : 0), 0);
      const avg = playerValues.length > 0 ? sum / playerValues.length : 0;
      return roundAverage(avg, metricKey);
    }
    
    return null;
  };

  // Build Antd columns
  const columns = useMemo(() => {
    const cols = [
      {
        title: "Position",
        dataIndex: "positionLabel",
        key: "positionLabel",
        fixed: 'left',
        width: 160,
        onCell: (record) => {
          if (record?.isPlayer) {
            return {
              style: { paddingLeft: 0 }
            };
          }
          if (!record?.isPlayer && !record?.isTeamAvg) {
            return {
              style: { backgroundColor: '#eff9ff' }
            };
          }
          return {};
        },
        render: (text, record) => {
          if (record?.isTeamAvg) {
            return (
              <Tooltip title="Team Avg is weighted by players and positions">
                <span>Team Avg*</span>
              </Tooltip>
            );
          }
          return text;
        },
      },
    ];
    filteredMetrics.forEach(({ key, name }) => {
      cols.push({
        title: name,
        dataIndex: key,
        key,
        width: 110,
        onCell: (record) => {
          const dirty = isDirtyCell(record.positionValue, key);
          return {
            className: dirty ? 'baseline-cell-dirty' : undefined,
            style: dirty ? { backgroundColor: '#fff9c4' } : undefined,
          };
        },
        render: (value, record) => {
          const dirty = isDirtyCell(record.positionValue, key);
          const previewValue = getPreviewValue(record, key);
          const hasPreview = previewValue !== null && previewValue !== undefined;
          
          const inputEl = (
            <div style={{ position: 'relative', width: '100%' }}>
              <PermissionedInputNumber
                type="number"
                min={0}
                step={1}
                size="small"
                value={value}
                onChange={(v) => onCellChange(record.positionValue, key, v, record.isPlayer, record.parentPosition)}
                permissionCheck="EditBaselines"
                className={dirty ? 'baseline-input-dirty' : undefined}
                style={{ 
                  width: '100%', 
                  backgroundColor: dirty ? '#fff9c4' : undefined,
                  paddingRight: hasPreview ? '50px' : undefined
                }}
                disabled={record.isTeamAvg === true}
              />
              {hasPreview && (
                <span
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#999',
                    fontSize: '10px',
                    pointerEvents: 'none',
                    fontStyle: 'italic'
                  }}
                >
                {previewValue}
                </span>
              )}
            </div>
          );
          if (record?.isTeamAvg) {
            return (
              <Tooltip title="Team Avg is weighted by players and positions">
                <span style={{ display: 'block' }}>{inputEl}</span>
              </Tooltip>
            );
          }
          return inputEl;
        },
      });
    });
    return cols;
  }, [filteredMetrics, dataSource, dirtyCounter]);

  const hasDirty = useMemo(() => {
    return dataSource.some((row) => {
      if (row.isTeamAvg) return false;
      
      // Check if position row has changes
      if ((dirtyMapRef.current[row.positionValue]?.size || 0) > 0) return true;
      
      // Check if any child rows have changes
      if (row?.children) {
        return row.children?.some((child) => (dirtyMapRef.current[child.positionValue]?.size || 0) > 0);
      }
      
      return false;
    });
  }, [dataSource]);

  return (
    <PermissionViewable 
      permissionCheck="ViewBaselines"
      permissionMessage="You don't have permission to view team baselines"
    >
      <div style={{ width: '100%', maxWidth: '100%' }} className="baseline-table">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: '16px' }}>
          <div style={{ flex: '1', maxWidth: '400px', marginTop: '10px' }}>
            <PresetSelection
              presets={presets || []}
              user={user}
              presetType={PRESET_TYPE}
              currStatMetrics={dataSource.filter(row => !row.isTeamAvg)}
              currentSelectedTilePreset={currentSelectedPreset}
              setCurrentSelectedTilePreset={setCurrentSelectedPreset}
              handleSave={handleSavePreset}
              warnUnsavedChanges={unsavedChanges}
              setWarnUnsavedChages={setUnsavedChanges}
              saveButtonLabel="Save Preset"
              width={null}
            />
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Tooltip title={hasDirty ? '' : 'No changes to save'}>
              <PermissionedButton
                type="primary"
                onClick={saveChanges}
                disabled={!hasDirty}
                loading={loading}
                permissionCheck="EditBaselines"
                style={{ minWidth: '140px' }}
              >
                Save Changes
              </PermissionedButton>
            </Tooltip>
            <PermissionedButton
              type="primary"
              onClick={() => {setBuildBaselineModalVisible(true)}}
              loading={loading}
              permissionCheck="EditBaselines"
              data-attr="settings-baselines-help-me-build-my-baselines"
            >
              Help me build my Baselines
            </PermissionedButton>
          </div>
        </div>
        {buildBaselineModalVisible &&
          <BuildBaselineModal
            visible={buildBaselineModalVisible}
            setVisibility={setBuildBaselineModalVisible}
            setRefetchData={setRefetchData}
            />
        }
        <Table
          size="small"
          loading={loading}
          rowKey="key"
          dataSource={dataSource}
          columns={columns}
          pagination={false}
          scroll={{ x: Math.max(800, 240 + filteredMetrics.length * 120) }}
          sticky
          rowClassName={(record) => {
            if (!record.isPlayer && !record.isTeamAvg) {
              return "baseline-position-row";
            }
            return "";
          }}
          expandable={{
            defaultExpandAllRows: false,
            expandRowByClick: false,
            rowExpandable: (record) => !!(record.children && record.children.length > 0),
          }}
        />
      </div>
    </PermissionViewable>
  );
};
